import * as PIXI from "pixi.js";
import { ObjectPool } from "../../HelperModules/ObjectPool.js";
import { ScrollableBar } from "./ScrollableBar.js";
import { MouseClickType, TimelineMode, EventSnapType } from "../Settings/Enums.js";
import { TimelineMarker } from "./TimelineMarker.js";
import { UIPositioning } from "../Settings/UITheme.js";
import { MouseTypeContainer } from "./InteractiveContainer.js";
import { SimpleEvent } from "../../HelperModules/SimpleEvent.js";
import { editType } from "../Settings/EditType.js";
export class ScrollableTimeline extends MouseTypeContainer {
    get dragType() {
        return editType.snapType;
    }
    constructor(startX, endX, startY, endY, songManager) {
        super();
        this.timelineMode = TimelineMode.Edit;
        this._zoomScale = 1;
        this._verticalScrollPosition = 0;
        this._mouseClickType = MouseClickType.None;
        this._selected = [];
        this.startX = startX;
        this.startY = startY;
        this.endX = endX;
        this.endY = endY;
        this.songManager = songManager;
        this._onScreen = true;
        this.timelineViewChange = new SimpleEvent();
        this._scrollObjects = [];
        this._barPool = new ObjectPool();
        this._maskGraphics = new PIXI.Graphics();
        this.mask = this._maskGraphics;
        this._maskGraphics.beginFill(0xFFFFFF).drawRect(this.startX, this.startY, this.endX, this.endY).endFill();
        this._interactivityRect = new PIXI.Graphics();
        this.addChild(this._interactivityRect);
        this.resizeInteractiveArea(endX, endY);
        this._barContainer = new PIXI.Container();
        this._headerContainer = new PIXI.Container();
        this._metadataEventContainer = new PIXI.Container();
        this._eventContainer = new PIXI.Container();
        this._timelineMarker = new TimelineMarker();
        this.addChild(this._barContainer, this._eventContainer, this._headerContainer, this._metadataEventContainer, this._timelineMarker);
        this._timelineMarkerAnim = this._timelineMarkerAnim.bind(this);
        this._playingStateChanged = this._playingStateChanged.bind(this);
        this._repositionTimelineMarker = this._repositionTimelineMarker.bind(this);
        this.songManager.playingChangedEvent.addListener(this._playingStateChanged);
    }
    get metadata() {
        return this.songManager.metadata;
    }
    get beatWidth() {
        return ScrollableTimeline.beatWidth * this._zoomScale;
    }
    resize(width, height) {
        this.resizeInteractiveArea(width, height);
        this._maskGraphics.clear().beginFill(0xFFFFFF).drawRect(this.startX, this.startY, width, height).endFill();
        this.endX = Math.max(width, this.startX + 1);
        this.endY = Math.max(height, this.startY + 1);
        let barHeight = Math.max(this.contentHeight, this.endY);
        for (let i = 0; i < this._barContainer.children.length; i++) {
            let bar = this._barContainer.children[i];
            bar.resize(barHeight);
        }
        let rightSideOffset = this.x + this._scrollObjects[this._scrollObjects.length - 1].rightBound - this.endX;
        while (rightSideOffset < 100) {
            let lastBar = this._scrollObjects[this._scrollObjects.length - 1];
            let bar = this._initialiseScrollableBar(lastBar.rightBound, lastBar.barNumber + 1, true);
            this._scrollObjects.push(bar);
            rightSideOffset = this.x + this._scrollObjects[this._scrollObjects.length - 1].rightBound - this.endX;
        }
        while (rightSideOffset > 800 && this._scrollObjects.length > 1) {
            let bar = this._scrollObjects.pop();
            this._returnScrollableBar(bar);
            rightSideOffset = this.x + this._scrollObjects[this._scrollObjects.length - 1].rightBound - this.endX;
        }
    }
    resizeInteractiveArea(width, height) {
        this._interactivityRect.clear();
        this._interactivityRect.beginFill(0x000000, 1.0);
        this._interactivityRect.drawRect(0, 0, width, height);
        this._interactivityRect.endFill();
        this._interactivityRect.alpha = 0.0;
    }
    pointerDownHandler(event) {
        super.pointerDownHandler(event);
        if (this._mouseClickType == MouseClickType.None) {
            return;
        }
        else if (this._mouseClickType == MouseClickType.LeftClick) {
            this.timelineViewChange.emit();
        }
        this._startPointerPosition = event.data.getLocalPosition(this.parent);
        this._startXPosition = this.x;
    }
    pointerMoveHandler(event) {
        if (this._mouseClickType == MouseClickType.RightClick && this.timelineMode == TimelineMode.Edit) {
        }
        else if (this._mouseClickType == MouseClickType.LeftClick) {
            let moveDelta = event.data.getLocalPosition(this.parent).x - this._startPointerPosition.x;
            this.x = this._startXPosition + moveDelta;
            if (this._scrollObjects[0].barNumber === 0 && this.x + this._scrollObjects[0].leftBound > this.startX) {
                this.x = -this._scrollObjects[0].leftBound + this.startX;
            }
            let anchor = this.x - this.endX;
            let rightSideOffset = anchor + this._scrollObjects[this._scrollObjects.length - 1].rightBound;
            while (rightSideOffset < 100) {
                let lastBar = this._scrollObjects[this._scrollObjects.length - 1];
                let bar = this._initialiseScrollableBar(lastBar.rightBound, lastBar.barNumber + 1, true);
                this._scrollObjects.push(bar);
                rightSideOffset = anchor + this._scrollObjects[this._scrollObjects.length - 1].rightBound;
            }
            rightSideOffset = anchor + this._scrollObjects[this._scrollObjects.length - 1].leftBound;
            while (rightSideOffset > 800) {
                let bar = this._scrollObjects.pop();
                this._returnScrollableBar(bar);
                rightSideOffset = anchor + this._scrollObjects[this._scrollObjects.length - 1].leftBound;
            }
            anchor = this.startX - this.x;
            let leftSideOffset = anchor - this._scrollObjects[0].leftBound;
            while (leftSideOffset < 100 && this._scrollObjects[0].barNumber != 0) {
                let firstBar = this._scrollObjects[0];
                let bar = this._initialiseScrollableBar(firstBar.leftBound, firstBar.barNumber - 1, false);
                this._scrollObjects.splice(0, 0, bar);
                leftSideOffset = anchor - this._scrollObjects[0].leftBound;
            }
            leftSideOffset = anchor - this._scrollObjects[0].rightBound;
            while (leftSideOffset > 800) {
                let bar = this._scrollObjects.splice(0, 1)[0];
                this._returnScrollableBar(bar);
                leftSideOffset = anchor - this._scrollObjects[0].rightBound;
            }
        }
    }
    pointerUpHandler(event) {
        super.pointerUpHandler(event);
        this._startXPosition = undefined;
        this._startPointerPosition = undefined;
    }
    pointerUpClickHandler(event) {
        if (this._mouseClickType == MouseClickType.LeftClick) {
            this.x = this._startXPosition;
            if ((this.timelineMode == TimelineMode.Playback || this._startPointerPosition.y < UIPositioning.timelineHeaderHeight) && this._startPointerPosition.x > this.startX) {
                let [barPosition, beatPosition, numberOfBeats] = this._getBarFromStageCoordinates(this._startPointerPosition.x);
                barPosition += beatPosition / numberOfBeats;
                this.songManager.quarterNotePosition = this.metadata.positionBarsToQuarterNote(barPosition);
            }
        }
    }
    pointerUpDragHandler(event) {
        if (this._mouseClickType == MouseClickType.LeftClick) {
            this._offsetChildren(-this.x);
            this.x = 0;
            if (editType.markerCentered) {
                editType.markerCentered = false;
            }
        }
    }
    mouseWheelHandler(event, canvasX, canvasY) {
        let stageX = event.clientX - canvasX;
        let stageY = event.clientY - canvasY;
        if (stageX < this.startX || stageX > this.endX || stageY < 0 || stageY > this.endY) {
            return;
        }
        this.timelineViewChange.emit();
        let [barPosition, beatPosition, numBeats] = this._getBarFromStageCoordinates(stageX);
        barPosition += beatPosition / numBeats;
        this._zoomScale = Math.max(0.5, Math.min(5.0, this._zoomScale - event.deltaY / 1000));
        this._regenerateTimeline(this._scrollObjects[0].barNumber, Math.floor(barPosition));
        let offset = this._getStageCoordinatesFromBar(barPosition) - stageX;
        if (this._scrollObjects[0].barNumber === 0 && this._scrollObjects[0].leftBound - offset > this.startX) {
            offset = this.startX - this._scrollObjects[0].leftBound;
        }
        this._offsetChildren(offset);
    }
    updateVerticalScroll(value) {
        this._scrollObjects.forEach(bar => {
            bar.verticalScrollPosition = value;
        });
        this._eventContainer.y = value;
        this._verticalScrollPosition = value;
    }
    regenerateAroundPosition(barPosition) {
        let startBarPosition = this._getStageCoordinatesFromBar(barPosition);
        if (startBarPosition === -1) {
            this._regenerateTimeline(Math.floor(barPosition));
        }
        else {
            this._regenerateTimeline(this._scrollObjects[0].barNumber, Math.floor(barPosition));
            let offset = this._getStageCoordinatesFromBar(barPosition) - startBarPosition;
            if (this._scrollObjects[0].barNumber === 0 && this._scrollObjects[0].leftBound - offset > this.startX) {
                offset = this.startX - this._scrollObjects[0].leftBound;
            }
            this._offsetChildren(offset);
        }
    }
    addedHandler() {
        this._onScreen = true;
        this.regenerateAroundPosition(this._scrollObjects[0].barNumber);
        this._playingStateChanged(this.songManager.playing);
    }
    removedHandler() {
        this._onScreen = false;
        this._playingStateChanged(false);
        this._mouseClickType = MouseClickType.None;
    }
    destroy() {
        this.songManager.playingChangedEvent.removeListener(this._playingStateChanged);
        super.destroy({ children: true });
    }
    _getBarFromStageCoordinates(stageX) {
        for (let i = 0; i < this._scrollObjects.length; i++) {
            if (this._scrollObjects[i].leftBound <= stageX && this._scrollObjects[i].rightBound > stageX) {
                return [this._scrollObjects[i].barNumber, ((stageX - this._scrollObjects[i].leftBound) / this.beatWidth), this._scrollObjects[i].numberOfBeats];
            }
        }
    }
    _getStageCoordinatesFromBar(barNumber) {
        for (let i = 0; i < this._scrollObjects.length; i++) {
            if (this._scrollObjects[i].barNumber === Math.floor(barNumber)) {
                return this._scrollObjects[i].leftBound + ((barNumber % 1) * this._scrollObjects[i].numberOfBeats * this.beatWidth);
            }
        }
        return -1;
    }
    _offsetChildren(pixelOffset) {
        this._scrollObjects.forEach(child => {
            child.setX(child.x - pixelOffset);
        });
        for (let i = 0; i < this._eventContainer.children.length; i++) {
            this._eventContainer.children[i].x -= pixelOffset;
        }
        for (let i = 0; i < this._metadataEventContainer.children.length; i++) {
            this._metadataEventContainer.children[i].x -= pixelOffset;
        }
        this._checkBarsFillScreen();
        this._repositionTimelineMarker(this.songManager.quarterNotePosition);
    }
    _regenerateTimeline(fromBar, toBar) {
        let currentBar = fromBar;
        while (this._scrollObjects.length > 0) {
            this._returnScrollableBar(this._scrollObjects[0]);
            this._scrollObjects.splice(0, 1);
        }
        let currentXPosition = this.startX;
        let generatedABar = false;
        while (currentXPosition < this.endX || !generatedABar) {
            generatedABar = true;
            let bar = this._initialiseScrollableBar(currentXPosition, currentBar, true);
            this._scrollObjects.push(bar);
            currentBar++;
            currentXPosition = bar.rightBound;
        }
        if (toBar != undefined) {
            while (currentBar <= toBar) {
                let bar = this._initialiseScrollableBar(currentXPosition, currentBar, true);
                this._scrollObjects.push(bar);
                currentBar++;
                currentXPosition = bar.rightBound;
            }
        }
        if (this._eventContainer.children.length == 0) {
            this._initialiseTrackTimelineEvents();
        }
        else {
            for (let i = 0; i < this._eventContainer.children.length; i++) {
                let event = this._eventContainer.children[i];
                event.reinitialise();
            }
        }
        this._redrawTimelineMarker();
    }
    _checkBarsFillScreen() {
        while (this._scrollObjects[0].leftBound > this.startX && this._scrollObjects[0].barNumber != 0) {
            let bar = this._initialiseScrollableBar(this._scrollObjects[0].leftBound, this._scrollObjects[0].barNumber - 1, false);
            this._scrollObjects.splice(0, 0, bar);
        }
        while (this._scrollObjects[this._scrollObjects.length - 1].rightBound < this.endX) {
            let bar = this._initialiseScrollableBar(this._scrollObjects[this._scrollObjects.length - 1].rightBound, this._scrollObjects[this._scrollObjects.length - 1].barNumber + 1, true);
            this._scrollObjects.push(bar);
        }
    }
    _scrollToPosition(barPosition) {
        let barNumber = Math.floor(barPosition);
        this._regenerateTimeline(barNumber);
        let scrollAmount = this.metadata.getTimeSignature(barPosition)[0] * (barPosition % 1) * this.beatWidth;
        this._offsetChildren(scrollAmount);
    }
    _initialiseScrollableBar(xPosition, barNumber, leftSide) {
        let bar = null;
        if (this._barPool.objectCount > 0) {
            bar = this._barPool.getObject();
            bar.setVisible(true);
        }
        else {
            bar = new ScrollableBar(this._headerContainer, this);
            this._barContainer.addChild(bar);
        }
        let quarterNotePosition = this.metadata.positionBarsToQuarterNote(barNumber);
        let numberOfBeats = this.metadata.getTimeSignature(quarterNotePosition)[0];
        let metadataEventActive = !(this.metadata.events.binarySearch(quarterNotePosition) == -1);
        bar.initialise(xPosition, Math.max(this.contentHeight, this.endY), barNumber, numberOfBeats, this.beatWidth, quarterNotePosition, metadataEventActive, leftSide);
        bar.verticalScrollPosition = this._verticalScrollPosition;
        return bar;
    }
    _returnScrollableBar(instance) {
        instance.setVisible(false);
        this._barPool.returnObject(instance);
    }
    snapCoordinateToDragType(value) {
        return value - this.getPixelOffsetFromDragType(value);
    }
    getPixelOffsetFromDragType(value) {
        switch (this.dragType) {
            case EventSnapType.Beat:
                return (value % this.beatWidth);
            case EventSnapType.HalfBeat:
                return (value % (this.beatWidth / 2));
            case EventSnapType.QuarterBeat:
                return (value % (this.beatWidth / 4));
            case EventSnapType.EighthBeat:
                return (value % (this.beatWidth / 8));
            case EventSnapType.None:
                return 0;
        }
    }
    snapBeatToDragType(value) {
        switch (this.dragType) {
            case EventSnapType.None:
                break;
            case EventSnapType.Beat:
                value = value - (value % 1);
                break;
            case EventSnapType.HalfBeat:
                value *= 2;
                value = (value - (value % 1)) / 2;
                break;
            case EventSnapType.QuarterBeat:
                value *= 4;
                value = (value - (value % 1)) / 4;
                break;
            case EventSnapType.EighthBeat:
                value *= 8;
                value = (value - (value % 1)) / 8;
                break;
        }
        return value;
    }
    getTimelineEventXWidth(startPosition, endPosition) {
        let x = this.getTimelineEventX(startPosition);
        let width = (this.metadata.positionQuarterNoteToBeats(endPosition) - this.metadata.positionQuarterNoteToBeats(startPosition)) * this.beatWidth;
        return [x, width];
    }
    getTimelineEventX(position) {
        return (this.metadata.positionQuarterNoteToBeats(position) - this.metadata.positionQuarterNoteToBeats(this.metadata.positionBarsToQuarterNote(this._scrollObjects[0].barNumber))) * this.beatWidth + this._scrollObjects[0].leftBound;
    }
    _playingStateChanged(value) {
        if (value == true && this._onScreen) {
            this.timelineMode = TimelineMode.Playback;
            requestAnimationFrame(this._timelineMarkerAnim);
        }
        else {
            this.timelineMode = TimelineMode.Edit;
            if (!this.songManager.quarterNotePositionChangedEvent.hasListener(this._repositionTimelineMarker)) {
                this.songManager.quarterNotePositionChangedEvent.addListener(this._repositionTimelineMarker);
            }
        }
    }
    _timelineMarkerAnim() {
        this._repositionTimelineMarker(this.songManager.quarterNotePosition);
        if (this.songManager.playing == true) {
            try {
                if (editType.markerCentered && (this._timelineMarker.x > this.endX || this._timelineMarker.x < this.startX)) {
                    let barPosition = this.metadata.positionQuarterNoteToBars(this.songManager.quarterNotePosition);
                    this._scrollToPosition(barPosition);
                }
                requestAnimationFrame(this._timelineMarkerAnim);
            }
            catch (e) {
                return;
            }
        }
    }
    _redrawTimelineMarker() {
        this._timelineMarker.redraw(0, UIPositioning.timelineHeaderHeight, 2, this.endY);
        this._repositionTimelineMarker(this.songManager.quarterNotePosition);
    }
    _repositionTimelineMarker(position) {
        if (this._onScreen) {
            this._timelineMarker.x = Math.round(this.getTimelineEventX(position));
        }
    }
}
ScrollableTimeline.beatWidth = 50;
//# sourceMappingURL=ScrollableTimeline.js.map